import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Quote, Calendar, Layers, Briefcase, Download, Lock } from "lucide-react";
import { caseStudies, services } from "../mock";
import useSeo from "../hooks/useSeo";
import useAuth from "../hooks/useAuth";
import PremiumGate from "../components/PremiumGate";
import { downloadPdf } from "../lib/downloadPdf";
import { useToast } from "../hooks/use-toast";

const findCase = (slug) => {
  for (const key of Object.keys(caseStudies)) {
    const found = caseStudies[key].find((c) => c.slug === slug);
    if (found) return { service: key, item: found };
  }
  return null;
};

const CaseStudyDetail = () => {
  const { slug } = useParams();
  const { isAuthenticated } = useAuth();
  const { toast } = useToast();
  const match = findCase(slug);
  useSeo({
    title: match ? `${match.item.client} — ${match.item.title}` : "Case Study",
    description: match?.item?.challenge || match?.item?.title,
    image: match?.item?.image,
    type: "article",
    path: `/case-studies/${slug}`,
  });
  if (!match) return <Navigate to="/services" replace />;
  const { service, item } = match;
  const svc = services.find((s) => s.id === service);
  const locked = item.premium && !isAuthenticated;

  return (
    <>
      <section className="relative bg-black text-white">
        <div className="absolute inset-0 opacity-40 bg-cover bg-center" style={{ backgroundImage: `url(${item.image})` }} />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/80 to-black/40" />
        <div className="relative max-w-6xl mx-auto px-6 py-20">
          <Link to="/services" className="inline-flex items-center gap-2 text-teal-400 hover:text-teal-300 text-sm font-semibold">
            <ArrowLeft className="w-4 h-4" /> Back to services
          </Link>
          <p className="mt-6 text-teal-400 font-bold tracking-widest text-xs">CASE STUDY · {svc?.title?.toUpperCase()}</p>
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            <h1 className="text-3xl md:text-5xl font-extrabold leading-tight max-w-3xl">{item.title}</h1>
            {item.premium && (
              <span className="inline-flex items-center gap-1 bg-amber-500 text-black text-xs font-bold tracking-widest px-3 py-1 rounded-full h-fit">
                <Lock className="w-3 h-3" /> PREMIUM
              </span>
            )}
          </div>
          <p className="mt-4 text-neutral-200 max-w-2xl">Client: <span className="font-semibold">{item.client}</span></p>
        </div>
      </section>

      {locked ? (
        <PremiumGate kind="case study" title={item.title} excerpt={item.challenge} image={item.image} />
      ) : (
        <>
        <div className="bg-white pt-8 no-print">
          <div className="max-w-6xl mx-auto px-6 flex justify-end">
            <button
              onClick={async () => {
                try {
                  const blocks = [
                    { type: "h", text: "The challenge" },
                    { type: "p", text: item.challenge },
                    { type: "h", text: "Our approach" },
                    { type: "p", text: item.approach },
                    { type: "h", text: "The outcome" },
                    { type: "p", text: item.outcome },
                    { type: "quote", text: `${item.quote} — ${item.author}` },
                    { type: "h", text: "Results" },
                    { type: "list", items: item.results.map((r) => `${r.value} — ${r.label}`) },
                    { type: "h", text: "Timeline" },
                    { type: "list", items: item.timeline.map((t) => `${t.phase} · ${t.title}: ${t.body}`) },
                  ];
                  await downloadPdf({
                    title: item.title,
                    kicker: `Case Study · ${svc?.title || ""}`,
                    meta: `${item.client} · ${item.industry} · ${item.duration}`,
                    blocks,
                  });
                } catch (e) {
                  toast({ title: "Download failed", description: "Please sign in again and retry." });
                }
              }}
              className="inline-flex items-center gap-2 bg-teal-500 hover:bg-teal-600 text-white text-sm font-semibold px-4 py-2 rounded-full transition-colors"
            >
              <Download className="w-4 h-4" /> Download PDF
            </button>
          </div>
        </div>

      <section className="bg-white py-14">
        <div className="max-w-6xl mx-auto px-6 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-neutral-50 rounded-2xl p-6 border border-neutral-100">
            <div className="flex items-center gap-2 text-teal-600"><Briefcase className="w-4 h-4" /><span className="text-xs font-bold tracking-widest">INDUSTRY</span></div>
            <p className="mt-2 font-semibold text-neutral-900">{item.industry}</p>
          </div>
          <div className="bg-neutral-50 rounded-2xl p-6 border border-neutral-100">
            <div className="flex items-center gap-2 text-teal-600"><Calendar className="w-4 h-4" /><span className="text-xs font-bold tracking-widest">DURATION</span></div>
            <p className="mt-2 font-semibold text-neutral-900">{item.duration}</p>
          </div>
          <div className="bg-neutral-50 rounded-2xl p-6 border border-neutral-100">
            <div className="flex items-center gap-2 text-teal-600"><Layers className="w-4 h-4" /><span className="text-xs font-bold tracking-widest">SERVICES</span></div>
            <p className="mt-2 font-semibold text-neutral-900">{item.services.join(", ")}</p>
          </div>
        </div>
      </section>

      <section className="bg-white pb-16">
        <div className="max-w-6xl mx-auto px-6 grid grid-cols-1 md:grid-cols-3 gap-6">
          {item.results.map((r) => (
            <div key={r.label} className="bg-black text-white rounded-2xl p-8 text-center">
              <div className="text-4xl font-extrabold text-teal-400">{r.value}</div>
              <div className="mt-2 text-xs uppercase tracking-widest text-neutral-300">{r.label}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-neutral-50 py-16">
        <div className="max-w-4xl mx-auto px-6 space-y-10">
          <div>
            <h2 className="text-2xl font-bold text-neutral-900">The challenge</h2>
            <p className="mt-3 text-neutral-700 leading-relaxed">{item.challenge}</p>
          </div>
          <div>
            <h2 className="text-2xl font-bold text-neutral-900">Our approach</h2>
            <p className="mt-3 text-neutral-700 leading-relaxed">{item.approach}</p>
          </div>
          <div>
            <h2 className="text-2xl font-bold text-neutral-900">The outcome</h2>
            <p className="mt-3 text-neutral-700 leading-relaxed">{item.outcome}</p>
          </div>

          <div className="bg-white rounded-2xl p-8 border border-neutral-100 relative">
            <Quote className="absolute -top-4 -left-2 w-8 h-8 text-teal-500" />
            <p className="text-lg text-neutral-800 italic leading-relaxed">“{item.quote}”</p>
            <p className="mt-4 text-sm font-semibold text-neutral-900">— {item.author}</p>
          </div>
        </div>
      </section>

      <section className="bg-white py-16">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-2xl font-bold text-neutral-900">Timeline</h2>
          <div className="mt-8 relative">
            <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-teal-200" />
            <div className="space-y-8">
              {item.timeline.map((t, i) => (
                <div key={t.phase} className={`relative md:grid md:grid-cols-2 md:gap-10 ${i % 2 === 0 ? "" : "md:[&>div:first-child]:col-start-2"}`}>
                  <div className="pl-12 md:pl-0 md:pr-10">
                    <div className="absolute left-2 md:left-1/2 -translate-x-1/2 w-5 h-5 rounded-full bg-teal-500 border-4 border-white shadow" />
                    <div className="bg-neutral-50 rounded-xl p-6 border border-neutral-100">
                      <p className="text-xs font-bold text-teal-600 tracking-widest">{t.phase}</p>
                      <h3 className="mt-1 text-lg font-bold text-neutral-900">{t.title}</h3>
                      <p className="mt-2 text-sm text-neutral-700 leading-relaxed">{t.body}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {item.gallery?.length > 0 && (
        <section className="bg-neutral-50 py-16">
          <div className="max-w-6xl mx-auto px-6">
            <h2 className="text-2xl font-bold text-neutral-900">Snapshots</h2>
            <div className="mt-6 grid grid-cols-2 md:grid-cols-3 gap-4">
              {item.gallery.map((src, i) => (
                <img key={i} src={src} alt={`case-${i}`} className="w-full h-56 object-cover rounded-2xl" />
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="bg-black text-white py-14 no-print">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h3 className="text-2xl md:text-3xl font-extrabold">Want a result like this?</h3>
          <Link to="/contact" className="mt-6 inline-flex items-center gap-2 bg-teal-500 hover:bg-teal-600 text-white font-semibold px-8 py-3 rounded-full transition-colors">
            Start a conversation <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
      </>
      )}
    </>
  );
};

export default CaseStudyDetail;
